import { useCallback, useEffect, useRef, useState } from 'react';
import { getRunningVersion, fetchLatestVersion, reloadWithFreshAssets } from '../utils/appUpdate';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const AUTO_UPDATE_PATHS = ['/', '/login', '/dashboard'];
const ATTEMPT_KEY = 'app_update_attempt';

// Controlla se esiste una versione piu' recente del frontend.
// Su login e dashboard aggiorna da solo; altrove segnala con un avviso, senza interrompere il lavoro.
export function useVersionCheck() {
    const [latestVersion, setLatestVersion] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const checkingRef = useRef(false);

    const check = useCallback(async () => {
        if (checkingRef.current) return;
        checkingRef.current = true;
        try {
            const running = getRunningVersion();
            if (!running) return; // sviluppo: nessuna versione di build
            const latest = await fetchLatestVersion();
            if (!latest) return;

            if (latest === running) {
                setLatestVersion(null);
                return;
            }
            setLatestVersion(latest);

            // Un solo tentativo automatico per versione: evita cicli di ricarica se i file non sono ancora allineati
            const alreadyTried = sessionStorage.getItem(ATTEMPT_KEY) === latest;
            if (!alreadyTried && AUTO_UPDATE_PATHS.includes(window.location.pathname)) {
                sessionStorage.setItem(ATTEMPT_KEY, latest);
                await reloadWithFreshAssets();
            }
        } catch (e) {
            // version.json non raggiungibile: nessun effetto sull'app
        } finally {
            checkingRef.current = false;
        }
    }, []);

    useEffect(() => {
        check();
        const interval = setInterval(check, CHECK_INTERVAL_MS);

        // Al ritorno in primo piano (tipico del telefono) l'avviso torna visibile e si ricontrolla subito
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                setDismissed(false);
                check();
            }
        };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [check]);

    return {
        updateAvailable: !!latestVersion && !dismissed,
        latestVersion,
        dismiss: () => setDismissed(true)
    };
}
